import React from "react";
import { SafeAreaView } from "react-native-safe-area-context";
import styled from "styled-components/native";
import { Boundary } from "@app/components/screen/boundary";
import { Platform } from "react-native";
import * as Updates from "expo-updates";
import { createLink } from "@app/util/link.utils";
import ListItem from "@app/components/questkit/listItem";
import { usePromise } from "@app/util/usePromise";
import { sentry } from "@app/util/sentry";
import { useEffectOnce } from "@app/util/useEffectOnce";

export const SettingsScreen: React.FC = () => {
  const { isUpdatePending } = Updates.useUpdates();
  const { execute: checkForNewVersion, isLoading: isCheckingForNewVersion } =
    usePromise(() =>
      __DEV__
        ? Promise.resolve()
        : Updates.checkForUpdateAsync()
            .then((update) => {
              if (update.isAvailable) {
                return Updates.fetchUpdateAsync();
              }
            })
            .catch((e) => {
              console.error("Error checking for new version", e);
              sentry.captureException(e, {
                extra: {
                  message:
                    "Failed to check for new app version when visiting settings page",
                  manifest: Updates.manifest,
                  updateId: Updates.updateId,
                  channel: Updates.channel,
                  runtimeVersion: Updates.runtimeVersion,
                },
              });
            })
    );

  useEffectOnce(() => {
    void checkForNewVersion();
  });

  return (
    <SettingsScreenWrapper>
      <SafeAreaView edges={["bottom"]}>
        <Boundary>
          <ListItem
            text="Account"
            icon="person"
            actionIcon="chevron-right"
            onPress={createLink({ screen: "Account" })}
          />
          <ListItem
            text="Apps"
            icon="cloud"
            actionIcon="chevron-right"
            onPress={createLink({ screen: "Apps" })}
          />
          {Platform.OS === "web" ? (
            /** WARNING: Do not remove this condition check, as this will result in our app from being
             *  permanently banned from the Apple App Store and Google Play Store
             */
            <ListItem
              text="Subscription & Billing"
              icon="money"
              actionIcon="chevron-right"
              onPress={createLink({ screen: "Billing" })}
            />
          ) : null}
          <ListItem
            text="Support & Feedback"
            icon="contact-support"
            actionIcon="chevron-right"
            onPress={createLink({ screen: "Support" })}
          />
          {isUpdatePending && !isCheckingForNewVersion ? (
            <ListItem
              text="Install update"
              icon="download"
              actionIcon="chevron-right"
              onPress={async () => {
                await Updates.reloadAsync();
              }}
            />
          ) : null}
        </Boundary>
      </SafeAreaView>
    </SettingsScreenWrapper>
  );
};

const SettingsScreenWrapper = styled.ScrollView.attrs({
  contentContainerStyle: {
    flex: 1,
    paddingHorizontal: 20,
    justifyContent: "center",
  },
})`
  background-color: ${({ theme }) => theme.background};
`;
